import React, { useEffect, useRef, useState } from 'react';
import styles from './visualiser-text-box.module.css';

type VisualiserTextBoxProps = {
  font?: string;
  fontWeight?: string;
  fontSize?: number;
  color?: string;
  lineHeight?: number;
  /** Size constraints for the text box. */
  size?: {
    width: string;
    minHeight: string;
    maxHeight: string;
    minWidth: string;
    maxWidth: string;
  };
  /** Position of the text box within the Visualiser. */
  position?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  maxLength?: number;
  maxLines?: number;
  initialValue?: string;
  userEditable?: boolean;
  value?: string;
  style?: React.CSSProperties;
  setValue: (value: string) => void;
}

export const VisualiserTextBox = ({
  font = "Figtree",
  fontWeight = "normal",
  fontSize = 21,
  lineHeight = 1.2,
  size,
  position,
  color,
  maxLength,
  maxLines,
  initialValue = "Tap to edit",
  userEditable = true,
  style,
  value,
  setValue
}: VisualiserTextBoxProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [adjustedFontSize, setAdjustedFontSize] = useState(fontSize);

  // Auto-resize textarea and adjust font size to respect maxHeight and maxLines.
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Parse maxHeight from size prop (e.g., "100px" -> 100).
    const maxHeightValue = size?.maxHeight ? parseFloat(size.maxHeight) : undefined;
    
    // Calculate effective max height based on maxLines if specified.
    let effectiveMaxHeight = maxHeightValue;
    if (maxLines) {
      // Calculate the height that maxLines would occupy.
      const computedLineHeight = lineHeight * fontSize;
      const maxLinesHeight = maxLines * computedLineHeight;
      
      // Use the smaller of maxHeight and maxLinesHeight.
      if (maxHeightValue) {
        effectiveMaxHeight = Math.min(maxHeightValue, maxLinesHeight);
      } else {
        effectiveMaxHeight = maxLinesHeight;
      }
    }

    const minFontSize = 8;
    let optimalFontSize = fontSize;

    // Find the optimal font size that fits the content.
    // Start from the current adjusted size or the default fontSize.
    let testFontSize = fontSize;
    
    // First, check if content fits at the original font size.
    textarea.style.fontSize = `${testFontSize}px`;
    textarea.style.height = 'auto';
    let scrollHeight = textarea.scrollHeight;

    // If content doesn't fit at original size, reduce until it fits.
    if (effectiveMaxHeight && scrollHeight > effectiveMaxHeight) {
      while (testFontSize > minFontSize) {
        testFontSize -= 0.5;
        textarea.style.fontSize = `${testFontSize}px`;
        textarea.style.height = 'auto';
        
        if (textarea.scrollHeight <= effectiveMaxHeight) {
          optimalFontSize = testFontSize;
          break;
        }
      }
      
      // If we've hit minimum font size and still doesn't fit.
      if (testFontSize <= minFontSize) {
        optimalFontSize = minFontSize;
      }
    } else {
      // Content fits at original size, but check if we can use a smaller size.
      // Try to increase from current adjusted size gradually.
      if (adjustedFontSize < fontSize && effectiveMaxHeight) {
        testFontSize = adjustedFontSize;
        
        // Gradually increase font size while content still fits.
        while (testFontSize < fontSize) {
          const nextSize = testFontSize + 0.5;
          textarea.style.fontSize = `${nextSize}px`;
          textarea.style.height = 'auto';
          
          if (textarea.scrollHeight <= effectiveMaxHeight) {
            testFontSize = nextSize;
          } else {
            break;
          }
        }
        
        optimalFontSize = testFontSize;
      }
    }

    // Apply the optimal font size.
    if (optimalFontSize !== adjustedFontSize) {
      setAdjustedFontSize(optimalFontSize);
    }
    
    textarea.style.fontSize = `${optimalFontSize}px`;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Adjust height whenever value changes.
  useEffect(() => {
    adjustHeight();
  }, [value, fontSize, size?.maxHeight, maxLines, lineHeight]);

  // Set initial value if value is undefined
  useEffect(() => {
    if (value === undefined) {
      setValue(initialValue);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    // Enforce allowed characters only (A-Z a-z 0-9 and specified special chars)
    const allowedCharsRegex = /[^A-Za-z0-9!@#$%^&*()_+\-=?.,:'"£¢`\s]/g;
    newValue = newValue.replace(allowedCharsRegex, '');

    // Enforce maxLength
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    // Enforce maxLines
    if (maxLines) {
      const lines = newValue.split('\n');
      if (lines.length > maxLines) {
        newValue = lines.slice(0, maxLines).join('\n');
      }
    }

    setValue(newValue);
  };

  return (
    <textarea
      ref={textareaRef}
      className={styles['visualiser-text-box']}
      disabled={!userEditable}
      autoComplete="off"
      autoCorrect="off"
      spellCheck="false"
      style={{
        position: 'absolute',
        top: position?.top,
        bottom: position?.bottom,
        left: position?.left,
        right: position?.right,
        width: size?.width || '100%',
        minWidth: size?.minWidth,
        maxWidth: size?.maxWidth,
        minHeight: size?.minHeight,
        maxHeight: size?.maxHeight,
        fontFamily: font,
        fontSize: `${adjustedFontSize}px`,
        fontWeight: fontWeight,
        opacity: !value ? 0.5 : 1,
        lineHeight: lineHeight,
        color: color || 'var(--color-black)',
        padding: font === 'Amalfi Coast' ? '8px 0 0 0' : '0',
        ...style,
      } as React.CSSProperties}
      placeholder={initialValue}
      value={value}
      onChange={handleChange}
      rows={1}
    />
  );
};